import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import StarField from "@/components/StarField";
import VerseModal from "@/components/VerseModal";
import GhostReplayModal from "@/components/GhostReplayModal";
import { randomVerse } from "@/data/verses";
import { motion } from "framer-motion";
import { Play, Pause, RotateCcw, ArrowLeft, ArrowRight, Eye, Flame } from "lucide-react";
import { recordVisit } from "@/lib/streak";
import { StreakCalendar } from "@/components/StreakCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Game constants
const LANES = 3;
const GAME_W = 360;
const GAME_H = 640;
const LANE_W = GAME_W / LANES;
const CAR_W = 46;
const CAR_H = 78;
const OBS_W = 60;
const OBS_H = 60;
const GOAL_DISTANCE = 3000; // total virtual distance to reach heaven

const OBSTACLE_LABELS = [
  "Doubt",
  "Fear",
  "Worry",
  "Anger",
  "Pride",
  "Regret",
  "Loss",
  "Envy",
  "Shame",
];

export default function GamePage() {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    lane: 1,
    obstacles: [], // {lane, y, label}
    lastSpawn: 0,
    distance: 0,
    speed: 0.28, // px per ms
    running: false,
    startTime: 0,
    lastFrame: 0,
    invulnUntil: 0,
    // Ghost car (async multiplayer)
    ghostFrames: null, // array of {t, lane}
    ghostLane: 1,
    // Local recording of this run's frames for future ghosts
    recordedFrames: [],
    lastRecordAt: 0,
    elapsedMs: 0,
  });
  const rafRef = useRef(null);
  const [ghostPresent, setGhostPresent] = useState(false);
  const [ghostReplayOpen, setGhostReplayOpen] = useState(false);

  const [status, setStatus] = useState("idle"); // idle | playing | paused | verse | heaven
  const [distance, setDistance] = useState(0);
  const [verse, setVerse] = useState(null);
  const [versesSeen, setVersesSeen] = useState(0);
  const [lastRefRef, setLastRefRef] = useState(null);
  const [streak, setStreak] = useState(0);
  const [streakReward, setStreakReward] = useState(null);
  const [visitDays, setVisitDays] = useState([]);

  useEffect(() => {
    const { count, reward, days } = recordVisit();
    setStreak(count);
    setVisitDays(days);
    if (reward) setStreakReward(reward);
  }, []);

  // Fetch a random ghost once on mount (and after reset)
  const loadGhost = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/game-ghosts/random`);
      const g = res.data;
      if (g && Array.isArray(g.frames) && g.frames.length > 1) {
        stateRef.current.ghostFrames = g.frames;
        stateRef.current.ghostLane = g.frames[0].lane;
        setGhostPresent(true);
      } else {
        stateRef.current.ghostFrames = null;
        setGhostPresent(false);
      }
    } catch {
      stateRef.current.ghostFrames = null;
      setGhostPresent(false);
    }
  }, []);

  useEffect(() => {
    loadGhost();
  }, [loadGhost]);

  const reset = useCallback(() => {
    stateRef.current = {
      lane: 1,
      obstacles: [],
      lastSpawn: 0,
      distance: 0,
      speed: 0.28,
      running: false,
      startTime: 0,
      lastFrame: 0,
      invulnUntil: 0,
      ghostFrames: stateRef.current.ghostFrames,
      ghostLane: stateRef.current.ghostFrames
        ? stateRef.current.ghostFrames[0].lane
        : 1,
      recordedFrames: [],
      lastRecordAt: 0,
      elapsedMs: 0,
    };
    setDistance(0);
    setVerse(null);
    setVersesSeen(0);
    setLastRefRef(null);
    setStatus("idle");
  }, []);

  const startGame = useCallback(() => {
    if (status === "heaven") reset();
    const now = performance.now();
    stateRef.current.running = true;
    stateRef.current.startTime = now;
    stateRef.current.lastFrame = now;
    stateRef.current.lastSpawn = now;
    stateRef.current.invulnUntil = now + 1500;
    setStatus("playing");
  }, [status, reset]);

  const pauseGame = useCallback(() => {
    if (status !== "playing") return;
    stateRef.current.running = false;
    setStatus("paused");
  }, [status]);

  const resumeGame = useCallback(() => {
    if (status !== "paused") return;
    const now = performance.now();
    stateRef.current.running = true;
    stateRef.current.lastFrame = now;
    setStatus("playing");
  }, [status]);

  const move = useCallback((dir) => {
    const s = stateRef.current;
    s.lane = Math.max(0, Math.min(LANES - 1, s.lane + dir));
  }, []);

  const submitGhost = useCallback(async (reached) => {
    const s = stateRef.current;
    const frames = s.recordedFrames || [];
    // Only save meaningful runs, and only once per run.
    if (s.ghostSaved) return;
    if (frames.length < 4 || s.distance < 400) return;
    s.ghostSaved = true;
    try {
      await axios.post(`${API}/game-ghosts`, {
        frames,
        distance: Math.round(s.distance),
        reached_heaven: reached,
      });
    } catch {
      s.ghostSaved = false; // allow a retry on next chance
    }
  }, []);

  const triggerVerse = useCallback(() => {
    const s = stateRef.current;
    s.running = false;
    const v = randomVerse(lastRefRef);
    setLastRefRef(v.reference);
    setVerse(v);
    setStatus("verse");
    setVersesSeen((n) => n + 1);
    try {
      localStorage.setItem(
        "rts_last_verse",
        JSON.stringify({ ...v, at: new Date().toISOString() }),
      );
    } catch {}
    // Contribute the run as a ghost for future travelers
    submitGhost(false);
  }, [lastRefRef, submitGhost]);

  const closeVerse = useCallback(() => {
    setVerse(null);
    // clear collided obstacles that are still overlapping to avoid immediate re-trigger
    const s = stateRef.current;
    s.obstacles = s.obstacles.filter((o) => o.y < 0 || o.y > GAME_H - CAR_H - 20);
    // resume
    const now = performance.now();
    s.running = true;
    s.lastFrame = now;
    s.invulnUntil = now + 1400;
    setStatus("playing");
  }, []);

  const submitRun = useCallback(async (reached) => {
    try {
      await axios.post(`${API}/game-runs`, {
        distance: Math.round(stateRef.current.distance),
        verses_seen: versesSeen,
        reached_heaven: reached,
      });
    } catch (e) {
      // silent
    }
  }, [versesSeen]);

  const reachHeaven = useCallback(() => {
    stateRef.current.running = false;
    setStatus("heaven");
    submitRun(true);
    submitGhost(true);
    // pull a fresh ghost for the next run
    loadGhost();
  }, [submitRun, submitGhost, loadGhost]);

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowLeft", "a", "A"].includes(e.key)) {
        move(-1);
        e.preventDefault();
      } else if (["ArrowRight", "d", "D"].includes(e.key)) {
        move(1);
        e.preventDefault();
      } else if (e.key === " " || e.key === "Enter") {
        if (status === "idle") startGame();
        else if (status === "playing") pauseGame();
        else if (status === "paused") resumeGame();
        e.preventDefault();
      } else if (e.key === "Escape") {
        if (status === "playing") pauseGame();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, startGame, pauseGame, resumeGame, status]);

  // Main loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let roadOffset = 0;

    const draw = () => {
      const s = stateRef.current;

      // background - dark to golden gradient as we progress
      const t = Math.min(1, s.distance / GOAL_DISTANCE);
      const bgTop = interpolateColor([5, 5, 10], [30, 20, 8], t);
      const bgBot = interpolateColor([15, 17, 26], [90, 60, 15], t);
      const grad = ctx.createLinearGradient(0, 0, 0, GAME_H);
      grad.addColorStop(0, bgTop);
      grad.addColorStop(1, bgBot);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, GAME_W, GAME_H);

      // heaven glow at top
      const glow = ctx.createRadialGradient(GAME_W / 2, -80, 20, GAME_W / 2, -80, 300);
      glow.addColorStop(0, `rgba(234,179,8,${0.15 + 0.55 * t})`);
      glow.addColorStop(1, "rgba(234,179,8,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, GAME_W, GAME_H);

      // road edges
      const roadPad = 20;
      ctx.fillStyle = "rgba(15,17,26,0.9)";
      ctx.fillRect(roadPad, 0, GAME_W - roadPad * 2, GAME_H);

      // side lines
      ctx.strokeStyle = "rgba(234,179,8,0.4)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(roadPad, 0);
      ctx.lineTo(roadPad, GAME_H);
      ctx.moveTo(GAME_W - roadPad, 0);
      ctx.lineTo(GAME_W - roadPad, GAME_H);
      ctx.stroke();

      // dashed lane markers
      ctx.strokeStyle = "rgba(255,255,255,0.35)";
      ctx.lineWidth = 3;
      ctx.setLineDash([22, 22]);
      for (let i = 1; i < LANES; i++) {
        const x = i * LANE_W;
        ctx.beginPath();
        ctx.moveTo(x, (roadOffset % 44) - 44);
        ctx.lineTo(x, GAME_H);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // obstacles
      for (const o of s.obstacles) {
        const x = o.lane * LANE_W + (LANE_W - OBS_W) / 2;
        const y = o.y;
        // shape: warning barrier
        ctx.fillStyle = "#1a1d29";
        roundRect(ctx, x, y, OBS_W, OBS_H, 8, true);
        ctx.strokeStyle = "#EAB308";
        ctx.lineWidth = 2;
        roundRect(ctx, x, y, OBS_W, OBS_H, 8, false, true);
        // stripes
        ctx.fillStyle = "rgba(234,179,8,0.85)";
        for (let i = 0; i < 3; i++) {
          ctx.fillRect(x + 6 + i * 16, y + OBS_H / 2 - 4, 10, 8);
        }
        // label
        ctx.fillStyle = "#F8F9FA";
        ctx.font = "500 10px 'JetBrains Mono', monospace";
        ctx.textAlign = "center";
        ctx.fillText(o.label.toUpperCase(), x + OBS_W / 2, y + 16);
      }

      // ghost car (async multiplayer replay)
      if (s.ghostFrames && s.ghostFrames.length > 1) {
        const gLane = s.ghostLane;
        const gx = gLane * LANE_W + (LANE_W - CAR_W) / 2;
        const gy = GAME_H - CAR_H - 24 - 130; // ahead of the player on the road
        drawGhostCar(ctx, gx, gy);
      }

      // car
      const carLane = s.lane;
      const cx = carLane * LANE_W + (LANE_W - CAR_W) / 2;
      const cy = GAME_H - CAR_H - 24;
      const invuln = performance.now() < s.invulnUntil;
      drawCar(ctx, cx, cy, invuln);

      // HUD - distance
      // (rendered outside canvas via React overlay)
      return;
    };

    const loop = (ts) => {
      const s = stateRef.current;
      if (s.running) {
        const dt = ts - s.lastFrame;
        s.lastFrame = ts;
        s.elapsedMs += dt;

        // advance distance
        s.distance += s.speed * dt;
        setDistance(s.distance);
        roadOffset += s.speed * dt;

        // speed ramp
        s.speed = 0.28 + Math.min(0.35, s.distance / 8000);

        // record own frames (sample every ~150ms, cap at 250)
        if (ts - s.lastRecordAt > 150 && s.recordedFrames.length < 250) {
          s.recordedFrames.push({ t: Math.round(s.elapsedMs), lane: s.lane });
          s.lastRecordAt = ts;
        }

        // interpolate ghost lane based on elapsed time
        if (s.ghostFrames && s.ghostFrames.length > 1) {
          const frames = s.ghostFrames;
          const last = frames[frames.length - 1];
          const tMs = s.elapsedMs % (last.t + 800); // gentle loop with pause
          // find bracketing frames
          let a = frames[0];
          let b = frames[frames.length - 1];
          for (let i = 0; i < frames.length - 1; i++) {
            if (frames[i].t <= tMs && frames[i + 1].t >= tMs) {
              a = frames[i];
              b = frames[i + 1];
              break;
            }
          }
          s.ghostLane = tMs >= last.t ? last.lane : a.lane;
          // (discrete lane switching is fine visually — no interpolation needed)
          // eslint-disable-next-line no-unused-vars
          void b;
        }

        // spawn obstacles
        const spawnInterval = Math.max(650, 1200 - s.distance / 6);
        if (ts - s.lastSpawn > spawnInterval) {
          const lane = Math.floor(Math.random() * LANES);
          // avoid stacking directly on top of an existing near-top obstacle same lane
          const conflict = s.obstacles.some(
            (o) => o.lane === lane && o.y < 120,
          );
          if (!conflict) {
            s.obstacles.push({
              lane,
              y: -OBS_H - Math.random() * 60,
              label:
                OBSTACLE_LABELS[
                  Math.floor(Math.random() * OBSTACLE_LABELS.length)
                ],
            });
            s.lastSpawn = ts;
          }
        }

        // move obstacles
        for (const o of s.obstacles) {
          o.y += s.speed * dt * 1.15;
        }
        // cull
        s.obstacles = s.obstacles.filter((o) => o.y < GAME_H + 40);

        // collision
        if (ts > s.invulnUntil) {
          const carLane = s.lane;
          const cy = GAME_H - CAR_H - 24;
          for (const o of s.obstacles) {
            if (o.lane === carLane) {
              const oy2 = o.y + OBS_H;
              if (oy2 > cy && o.y < cy + CAR_H) {
                triggerVerse();
                break;
              }
            }
          }
        }

        // reached heaven?
        if (s.distance >= GOAL_DISTANCE) {
          reachHeaven();
        }
      }

      draw();
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [triggerVerse, reachHeaven]);

  const progressPct = Math.min(100, (distance / GOAL_DISTANCE) * 100);

  return (
    <main
      data-testid="game-page"
      className="relative min-h-screen pt-28 pb-16 px-4 md:px-8 overflow-hidden"
    >
      <StarField count={70} />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]"
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-6xl mx-auto grid md:grid-cols-12 gap-10">
        {/* HUD / Left column */}
        <div className="md:col-span-4 order-2 md:order-1 flex flex-col gap-6">
          <div className="rts-glass rounded-xl p-6" data-testid="hud-panel">
            <p className="font-mono-rts text-[0.68rem] tracking-[0.35em] uppercase text-[#EAB308]">
              Journey Progress
            </p>
            <div
              className="mt-4 h-2 w-full rounded-full bg-white/5 overflow-hidden"
              data-testid="progress-bar"
            >
              <div
                className="h-full rounded-full transition-[width] duration-200"
                style={{
                  width: `${progressPct}%`,
                  background:
                    "linear-gradient(90deg, #1e3a8a 0%, #EAB308 100%)",
                  boxShadow: "0 0 14px rgba(234,179,8,0.5)",
                }}
              />
            </div>
            <div className="mt-3 flex items-center justify-between font-mono-rts text-xs text-white/70">
              <span data-testid="hud-distance">
                {Math.round(distance).toLocaleString()} / {GOAL_DISTANCE.toLocaleString()}
              </span>
              <span data-testid="hud-verses">Verses: {versesSeen}</span>
            </div>
            <div
              className="mt-4 text-[0.65rem] font-mono-rts tracking-[0.25em] uppercase text-[#EAB308]/80"
              data-testid="hud-streak"
            >
              <span className="flex items-center gap-2">
                <Flame className="w-3.5 h-3.5 shrink-0" />
                Day {streak} on the road
              </span>
              {streak % 7 !== 0 && (
                <span className="block mt-1 pl-[1.375rem] text-white/40 normal-case tracking-normal text-xs">
                  {7 - (streak % 7)} more day{7 - (streak % 7) === 1 ? "" : "s"} to a special verse
                </span>
              )}
            </div>
            {ghostPresent && (
              <div
                className="mt-4 flex items-center gap-2 text-[0.65rem] font-mono-rts tracking-[0.25em] uppercase text-blue-300/80"
                data-testid="hud-ghost-indicator"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-blue-300/80 animate-pulse" />
                A fellow traveler is on the road
              </div>
            )}
            <StreakCalendar days={visitDays} />
          </div>

          <div className="rts-glass rounded-xl p-6">
            <p className="font-mono-rts text-[0.68rem] tracking-[0.35em] uppercase text-[#EAB308]">
              Controls
            </p>
            <ul className="mt-4 space-y-2 text-sm text-white/75">
              <li className="flex items-center justify-between">
                <span>Steer</span>
                <span className="font-mono-rts text-xs text-white/50">
                  ← / → or A / D
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>Pause / Resume</span>
                <span className="font-mono-rts text-xs text-white/50">Space</span>
              </li>
              <li className="flex items-center justify-between">
                <span>Mobile</span>
                <span className="font-mono-rts text-xs text-white/50">
                  Tap lane buttons
                </span>
              </li>
            </ul>
          </div>

          <div className="flex flex-wrap gap-3">
            {status === "idle" || status === "heaven" ? (
              <button
                onClick={startGame}
                className="rts-btn-primary"
                data-testid="game-start-btn"
              >
                <Play className="w-4 h-4" />
                {status === "heaven" ? "Drive again" : "Start Driving"}
              </button>
            ) : status === "playing" ? (
              <button
                onClick={pauseGame}
                className="rts-btn-primary"
                data-testid="game-pause-btn"
              >
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : status === "paused" ? (
              <button
                onClick={resumeGame}
                className="rts-btn-primary"
                data-testid="game-resume-btn"
              >
                <Play className="w-4 h-4" /> Resume
              </button>
            ) : null}

            {status !== "idle" && (
              <button
                onClick={reset}
                className="rts-btn-ghost"
                data-testid="game-reset-btn"
              >
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            )}
          </div>

          <Link
            to="/prayer-line"
            className="rts-btn-ghost self-start"
            data-testid="game-prayerline-link"
          >
            Need someone to pray with you?
          </Link>

          <button
            type="button"
            onClick={() => setGhostReplayOpen(true)}
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-blue-300/80 hover:text-blue-200 border-b border-transparent hover:border-blue-200 pb-1 transition-colors duration-300 self-start"
            data-testid="game-watch-ghost-btn"
          >
            <Eye className="w-3.5 h-3.5" /> Watch a fellow traveler
          </button>
        </div>

        {/* Canvas / Right column */}
        <div className="md:col-span-8 order-1 md:order-2 flex flex-col items-center">
          <div
            className="relative rounded-2xl overflow-hidden border border-white/15"
            style={{
              boxShadow:
                "0 0 60px rgba(234,179,8,0.18), inset 0 0 0 1px rgba(255,255,255,0.05)",
              width: "100%",
              maxWidth: `${GAME_W}px`,
            }}
            data-testid="game-canvas-frame"
          >
            <canvas
              ref={canvasRef}
              width={GAME_W}
              height={GAME_H}
              className="block w-full h-auto bg-[#05050A]"
              data-testid="game-canvas"
            />

            {status === "idle" && (
              <IdleOverlay onStart={startGame} />
            )}
            {status === "paused" && (
              <PauseOverlay onResume={resumeGame} onReset={reset} />
            )}
            {status === "heaven" && (
              <HeavenOverlay
                onRestart={startGame}
                versesSeen={versesSeen}
                onWatchGhost={() => setGhostReplayOpen(true)}
              />
            )}
          </div>

          {/* Mobile lane buttons */}
          <div
            className="mt-6 flex items-center gap-4 md:hidden"
            data-testid="mobile-controls"
          >
            <button
              onClick={() => move(-1)}
              className="rts-glass rounded-full w-14 h-14 flex items-center justify-center border border-white/10 active:border-[#EAB308]"
              aria-label="Move left"
              data-testid="mobile-left-btn"
            >
              <ArrowLeft className="w-6 h-6 text-[#EAB308]" />
            </button>
            <button
              onClick={() => move(1)}
              className="rts-glass rounded-full w-14 h-14 flex items-center justify-center border border-white/10 active:border-[#EAB308]"
              aria-label="Move right"
              data-testid="mobile-right-btn"
            >
              <ArrowRight className="w-6 h-6 text-[#EAB308]" />
            </button>
          </div>
        </div>
      </div>

      <VerseModal open={status === "verse"} verse={verse} onClose={closeVerse} />
      <VerseModal
        open={!!streakReward && status !== "verse"}
        verse={streakReward}
        onClose={() => setStreakReward(null)}
      />
      <GhostReplayModal
        open={ghostReplayOpen}
        onClose={() => setGhostReplayOpen(false)}
      />
    </main>
  );
}

/* ---------- Overlays ---------- */

function IdleOverlay({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-[#05050A]/80 backdrop-blur-sm text-center px-6"
      data-testid="game-idle-overlay"
    >
      <p className="font-mono-rts text-[0.7rem] tracking-[0.4em] uppercase text-[#EAB308]">
        Ready
      </p>
      <h3 className="font-serif-rts text-3xl font-light">Take the wheel</h3>
      <p className="text-white/60 text-sm max-w-xs">
        Steer with arrow keys or A / D. When you hit a roadblock, a word will
        meet you there.
      </p>
      <button
        onClick={onStart}
        className="rts-btn-primary"
        data-testid="overlay-start-btn"
      >
        <Play className="w-4 h-4" /> Start
      </button>
    </motion.div>
  );
}

function PauseOverlay({ onResume, onReset }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-[#05050A]/80 backdrop-blur-sm text-center px-6"
      data-testid="game-paused-overlay"
    >
      <h3 className="font-serif-rts text-3xl font-light">Paused</h3>
      <div className="flex gap-3">
        <button
          onClick={onResume}
          className="rts-btn-primary"
          data-testid="overlay-resume-btn"
        >
          <Play className="w-4 h-4" /> Resume
        </button>
        <button
          onClick={onReset}
          className="rts-btn-ghost"
          data-testid="overlay-reset-btn"
        >
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
      </div>
    </motion.div>
  );
}

function HeavenOverlay({ onRestart, versesSeen, onWatchGhost }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-gradient-to-b from-[#05050A]/70 via-[#3a2a08]/70 to-[#EAB308]/40 backdrop-blur-sm text-center px-6"
      data-testid="game-heaven-overlay"
    >
      <p className="font-mono-rts text-[0.7rem] tracking-[0.4em] uppercase text-[#EAB308]">
        You made it
      </p>
      <h3 className="font-serif-rts text-4xl font-light">Welcome home.</h3>
      <p className="text-white/80 text-sm max-w-xs">
        You crossed the long dark road and heard{" "}
        <span className="text-[#EAB308]">{versesSeen}</span>{" "}
        {versesSeen === 1 ? "word" : "words"} along the way.
      </p>
      <div className="flex flex-col items-center gap-3">
        <button
          onClick={onRestart}
          className="rts-btn-primary"
          data-testid="overlay-restart-btn"
        >
          <RotateCcw className="w-4 h-4" /> Drive again
        </button>
        <button
          onClick={onWatchGhost}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-blue-300/80 hover:text-blue-200 border-b border-transparent hover:border-blue-200 pb-1 transition-colors duration-300"
          data-testid="overlay-watch-ghost-btn"
        >
          <Eye className="w-3.5 h-3.5" /> Watch a fellow traveler&rsquo;s run
        </button>
      </div>
    </motion.div>
  );
}

/* ---------- Canvas helpers ---------- */

function drawGhostCar(ctx, x, y) {
  ctx.save();
  ctx.globalAlpha = 0.4;
  // ethereal glow behind
  const glow = ctx.createRadialGradient(
    x + CAR_W / 2,
    y + CAR_H / 2,
    4,
    x + CAR_W / 2,
    y + CAR_H / 2,
    56,
  );
  glow.addColorStop(0, "rgba(147,197,253,0.7)");
  glow.addColorStop(1, "rgba(147,197,253,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(x - 30, y - 30, CAR_W + 60, CAR_H + 60);
  // body — cool blue
  const bodyGrad = ctx.createLinearGradient(x, y, x, y + CAR_H);
  bodyGrad.addColorStop(0, "#93C5FD");
  bodyGrad.addColorStop(1, "#3B82F6");
  ctx.fillStyle = bodyGrad;
  roundRect(ctx, x, y, CAR_W, CAR_H, 10, true);
  // roof
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  roundRect(ctx, x + 6, y + 16, CAR_W - 12, 30, 4, true);
  // outline dashed to feel spectral
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = "rgba(191,219,254,0.8)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, x, y, CAR_W, CAR_H, 10, false, true);
  ctx.setLineDash([]);
  ctx.restore();
}

function drawCar(ctx, x, y, invuln) {
  ctx.save();
  if (invuln && Math.floor(performance.now() / 120) % 2 === 0) {
    ctx.globalAlpha = 0.55;
  }
  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.6)";
  ctx.beginPath();
  ctx.ellipse(x + CAR_W / 2, y + CAR_H + 4, CAR_W / 2, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  // body
  const bodyGrad = ctx.createLinearGradient(x, y, x, y + CAR_H);
  bodyGrad.addColorStop(0, "#F59E0B");
  bodyGrad.addColorStop(1, "#B45309");
  ctx.fillStyle = bodyGrad;
  roundRect(ctx, x, y, CAR_W, CAR_H, 10, true);
  // roof
  ctx.fillStyle = "#1a1d29";
  roundRect(ctx, x + 6, y + 16, CAR_W - 12, 30, 4, true);
  // windshield highlight
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  roundRect(ctx, x + 8, y + 18, CAR_W - 16, 12, 3, true);
  // headlights
  ctx.fillStyle = "#FEF3C7";
  ctx.fillRect(x + 4, y + 2, 8, 4);
  ctx.fillRect(x + CAR_W - 12, y + 2, 8, 4);
  // headlight beams
  const beam = ctx.createLinearGradient(x, y - 60, x, y);
  beam.addColorStop(0, "rgba(254,243,199,0)");
  beam.addColorStop(1, "rgba(254,243,199,0.35)");
  ctx.fillStyle = beam;
  ctx.beginPath();
  ctx.moveTo(x + 4, y);
  ctx.lineTo(x + CAR_W - 4, y);
  ctx.lineTo(x + CAR_W + 12, y - 70);
  ctx.lineTo(x - 12, y - 70);
  ctx.closePath();
  ctx.fill();
  // taillights
  ctx.fillStyle = "#EF4444";
  ctx.fillRect(x + 4, y + CAR_H - 6, 8, 4);
  ctx.fillRect(x + CAR_W - 12, y + CAR_H - 6, 8, 4);
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
}

function interpolateColor(a, b, t) {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
